import {User, Info, MessageCircle} from "lucide-react";
import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {useIsMobile} from "@/hooks/use-mobile";
import {Dialog, DialogContent, DialogDescription, DialogTitle} from "@/components/ui/dialog";
import {FavoriteButton} from "@/components/ui/favorite-button";
import {Sheet, SheetContent, SheetTitle, SheetDescription} from "@/components/ui/sheet";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {Badge} from "@/components/ui/badge";
import {ScrollArea} from "@/components/ui/scroll-area";
import {PersonaDetailsTab} from "./persona-details-tab";
import {PersonaChatRuntimeProvider} from "./persona-chat-runtime-provider";
import {PersonaChatThread} from "./persona-chat-thread";
import {getPsychotypeColor, type AiPersona} from "@/lib/personas-api";

export type PersonaModalTab = "details" | "chat";

interface PersonaModalProps {
  persona: AiPersona | null;
  isOpen: boolean;
  onClose: () => void;
  activeTab: PersonaModalTab;
  onTabChange: (tab: PersonaModalTab) => void;
  /** Location ID from filters for chat context */
  locationId?: string | null;
  /** Date from filters for chat context */
  date?: string | null;
  /** Callback to add persona to favorites */
  onAddToFavorites?: () => void;
  /** Whether the add to favorites action is in progress */
  isAddingToFavorites?: boolean;
  /** Whether the persona is already a favorite */
  isFavorite?: boolean;
  /** AI persona database ID - required for chat persistence */
  aiPersonaId?: string;
}

interface ModalHeaderProps {
  persona: AiPersona;
  psychotypeColorClass: string;
  onAddToFavorites?: () => void;
  isAddingToFavorites?: boolean;
  isFavorite?: boolean;
  isMobile?: boolean;
}

function ModalHeader({
  persona,
  psychotypeColorClass,
  onAddToFavorites,
  isAddingToFavorites,
  isFavorite,
  isMobile = false,
}: ModalHeaderProps) {
  return (
    <div
      className={cn(
        "border-border/50 bg-muted/30 shrink-0 border-b px-4 py-4 sm:px-6 sm:py-5",
        isMobile && "pt-14" // Extra top padding for close button on mobile sheet
      )}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Avatar */}
        <div className="from-muted to-muted/50 ring-border/50 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ring-1 sm:h-14 sm:w-14">
          <User className="text-muted-foreground h-6 w-6 sm:h-7 sm:w-7" />
        </div>

        {/* Name and occupation */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {onAddToFavorites && (
              <FavoriteButton
                isFavorited={isFavorite}
                onClick={onAddToFavorites}
                disabled={isAddingToFavorites}
                size="md"
              />
            )}
            <h2 className="truncate text-lg font-semibold tracking-tight sm:text-xl">
              {persona.name}
            </h2>
            <Badge variant="outline" className={cn("shrink-0 font-medium", psychotypeColorClass)}>
              {persona.psychotype}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1 truncate text-sm">{persona.occupation}</p>
          <p className="text-muted-foreground/70 mt-0.5 text-sm">
            {persona.age_range} · {persona.gender}
          </p>
        </div>
      </div>
    </div>
  );
}

interface ModalContentProps {
  persona: AiPersona;
  activeTab: PersonaModalTab;
  onTabChange: (tab: PersonaModalTab) => void;
  locationId?: string | null;
  date?: string | null;
  aiPersonaId: string;
}

function ModalContent({
  persona,
  activeTab,
  onTabChange,
  locationId,
  date,
  aiPersonaId,
}: ModalContentProps) {
  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => onTabChange(v as PersonaModalTab)}
      className="flex min-h-0 flex-1 flex-col"
    >
      <TabsList className="bg-background h-11 w-full shrink-0 gap-1 rounded-none px-4 pb-2 sm:px-6">
        <TabsTrigger value="details">
          <Info className="h-4 w-4" />
          {m.persona_modal_tab_details()}
        </TabsTrigger>
        <TabsTrigger value="chat">
          <MessageCircle className="h-4 w-4" />
          {m.persona_modal_tab_chat()}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="details" className="mt-0 flex min-h-0 flex-1 flex-col">
        <ScrollArea className="flex min-h-0 flex-1 flex-col">
          <PersonaDetailsTab profile={persona} className="px-4 pb-6 sm:px-6" />
        </ScrollArea>
      </TabsContent>

      <TabsContent value="chat" className="mt-0 flex min-h-0 flex-1 flex-col px-4 pb-6 sm:px-6">
        <PersonaChatRuntimeProvider
          profile={persona}
          locationId={locationId}
          date={date}
          aiPersonaId={aiPersonaId}
        >
          {({onClearHistory}) => (
            <PersonaChatThread profile={persona} onClearHistory={onClearHistory} />
          )}
        </PersonaChatRuntimeProvider>
      </TabsContent>
    </Tabs>
  );
}

export function PersonaModal({
  persona,
  isOpen,
  onClose,
  activeTab,
  onTabChange,
  locationId,
  date,
  onAddToFavorites,
  isAddingToFavorites,
  isFavorite,
  aiPersonaId,
}: PersonaModalProps) {
  const isMobile = useIsMobile();

  if (!persona || !aiPersonaId) return null;

  const psychotypeColorClass = getPsychotypeColor(persona.psychotype);
  const title = persona.name;
  const description = `${persona.name} - ${persona.occupation}`;

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="bottom" className="flex h-[85vh] flex-col p-0">
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <SheetDescription className="sr-only">{description}</SheetDescription>
          <ModalHeader
            persona={persona}
            psychotypeColorClass={psychotypeColorClass}
            onAddToFavorites={onAddToFavorites}
            isAddingToFavorites={isAddingToFavorites}
            isFavorite={isFavorite}
            isMobile
          />
          <ModalContent
            persona={persona}
            activeTab={activeTab}
            onTabChange={onTabChange}
            locationId={locationId}
            date={date}
            aiPersonaId={aiPersonaId}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex h-[85vh] max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-2xl"
        showCloseButton
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>
        <ModalHeader
          persona={persona}
          psychotypeColorClass={psychotypeColorClass}
          onAddToFavorites={onAddToFavorites}
          isAddingToFavorites={isAddingToFavorites}
          isFavorite={isFavorite}
        />
        <ModalContent
          persona={persona}
          activeTab={activeTab}
          onTabChange={onTabChange}
          locationId={locationId}
          date={date}
          aiPersonaId={aiPersonaId}
        />
      </DialogContent>
    </Dialog>
  );
}
